import { memo } from 'react'

import { useTranslation } from 'react-i18next'

import {
  Box,
  ColorScheme,
  Flex,
  SIDE_NAV_WIDTH_CLOSED,
  SIDE_NAV_WIDTH_OPEN,
  UserAvatar,
} from '@hivewatch/dark'

import { useAppSelector } from '@/redux/hooks'
import { selectFullName } from '@/redux/me/meSlice'
import { selectSideNavOpen } from '@/redux/ui/uiSlice'
import { tVar } from '@/utils/i18n'

import { PROFILE_CARD_STYLES } from './constants'

interface ProfileInfoIProps {
  fullName: string
  colorScheme: ColorScheme
}

const ProfileInfo = memo(({ fullName, colorScheme }: ProfileInfoIProps) => {
  const { t } = useTranslation()
  const prefix = colorScheme === 'light' ? 'light.' : ''

  return (
    <Flex
      direction='column'
      gap={PROFILE_CARD_STYLES.TEXT_BLOCK_GAP}
      minWidth={0}
    >
      <Box
        color={`${prefix}text.secondary`}
        textStyle='caption'
        textTransform='uppercase'
      >
        {t('common:logged_in_as')}
      </Box>
      <Box color={`${prefix}text.primary`} lineClamp={1} textStyle='body-bold'>
        {fullName}
      </Box>
    </Flex>
  )
})

ProfileInfo.displayName = 'ProfileInfo'

interface SideBarProfileCardIProps {
  colorScheme?: ColorScheme
  /**
   * Supplied by `UserMenu`'s trigger render prop. They belong on this element
   * specifically because it is the one that takes focus — Chakra's
   * `PopoverTrigger` used to put them on the wrapper instead, where assistive
   * tech never saw them.
   */
  'aria-haspopup'?: 'menu'
  'aria-expanded'?: boolean
}

/**
 * The side nav's account row — the trigger for {@link SideBarProfile}'s menu.
 * Click handling lives on the wrapper the menu's `trigger` render prop
 * supplies, which is why this renders no `onClick` of its own.
 */
export const SideBarProfileCard = memo(
  ({
    colorScheme = 'dark',
    'aria-haspopup': ariaHasPopup,
    'aria-expanded': isExpanded,
  }: SideBarProfileCardIProps) => {
    const { t } = useTranslation()
    const fullName = useAppSelector(selectFullName)
    const isSideNavOpen = useAppSelector(selectSideNavOpen)

    const prefix = colorScheme === 'light' ? 'light.' : ''
    const width = `calc(${
      isSideNavOpen ? SIDE_NAV_WIDTH_OPEN : SIDE_NAV_WIDTH_CLOSED
    } - ${PROFILE_CARD_STYLES.OFFSET})`

    return (
      <Flex
        alignItems='center'
        aria-expanded={isExpanded}
        aria-haspopup={ariaHasPopup}
        aria-label={tVar(t, 'common:profile_menu_open_aria', { fullName })}
        bg={`${prefix}bg.sidebar-account`}
        // 8px per the design — the account row keeps a radius even though the
        // User Menu it opens is sharp on all four corners.
        borderRadius={PROFILE_CARD_STYLES.BORDER_RADIUS}
        cursor='pointer'
        data-testid='sidebar_profile_card'
        justifyContent={isSideNavOpen ? 'flex-start' : 'center'}
        m={PROFILE_CARD_STYLES.MARGIN}
        minHeight={
          isSideNavOpen
            ? PROFILE_CARD_STYLES.MIN_HEIGHT_EXPANDED
            : PROFILE_CARD_STYLES.MIN_HEIGHT_COLLAPSED
        }
        p={
          isSideNavOpen
            ? PROFILE_CARD_STYLES.PADDING_EXPANDED
            : PROFILE_CARD_STYLES.PADDING_COLLAPSED
        }
        role='button'
        tabIndex={0}
        transition={PROFILE_CARD_STYLES.TRANSITION}
        w={width}
      >
        <Flex alignItems='center' gap={PROFILE_CARD_STYLES.AVATAR_TEXT_GAP}>
          <UserAvatar
            bgColor={`${prefix}bg.user-badge`}
            colorScheme={colorScheme}
            name={fullName}
            size={isSideNavOpen ? 'lg' : 'md'}
            textColor={
              colorScheme === 'light'
                ? 'light.text.on-accent'
                : 'text.on-accent'
            }
          />
          {isSideNavOpen && (
            <ProfileInfo colorScheme={colorScheme} fullName={fullName} />
          )}
        </Flex>
      </Flex>
    )
  }
)

SideBarProfileCard.displayName = 'SideBarProfileCard'
